import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";
import { useParams, useLocation, useSearch } from "wouter";
import { Layout } from "@/components/layout";
import { useProductsLookup } from "@/hooks/use-products";
import { useOrder } from "@/hooks/use-orders";
import { NewOrderFlow } from "@/components/orders/NewOrderFlow";
import { usePcOrderScope } from "@/components/pc/PcOrderScopeProvider";
import { PcOrderingForBanner } from "@/components/pc/PcOrderingForBanner";
import { usePcHomeRoute } from "@/hooks/use-will-call";
import type { SelectedProduct } from "@/types/product";

export default function EditOrderPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const search = useSearch();
  const pcScope = usePcOrderScope();
  const pcHomeRoute = usePcHomeRoute();

  const viewMode = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get("mode") === "view";
  }, [search]);

  const { data: order, isLoading: orderLoading } = useOrder(id || "");

  const orderProductIds = useMemo(() => {
    if (!order?.products) return [];
    return order.products.map((p: any) => p.productId || p.uid).filter(Boolean);
  }, [order]);

  const {
    products: orderProducts,
    isLoading: orderProductsLoading,
  } = useProductsLookup(orderProductIds, orderProductIds.length > 0);

  const handleCancel = () => navigate(pcScope ? pcHomeRoute : "/orders");
  // Go to a fresh create page so the next order doesn't reuse this order's id
  const handleStartNewOrder = () =>
    navigate(pcScope ? "/pc/orders/new" : "/orders/new");
  // Swap this URL (no new history entry) to the read-only view of the submitted order
  const handleOrderSubmitted = () =>
    navigate(`/orders/edit/${id}?mode=view`, { replace: true });

  if (!id) {
    return (
      <Layout headerContent={
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("common.orders")}</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">{t("pages.editOrder.order")}</span>
        </div>
      }>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">{t("pages.editOrder.invalidOrderId")}</h2>
            <p className="text-gray-600">{t("pages.editOrder.noOrderIdProvided")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const productsResolved = orderProductIds.length === 0 || orderProducts.length > 0;
  const isLoading = orderLoading || (orderProductIds.length > 0 && orderProductsLoading) || (order && !productsResolved);

  if (isLoading) {
    return (
      <Layout headerContent={
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("common.orders")}</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">{viewMode ? t("pages.editOrder.viewOrder") : t("pages.editOrder.editOrder")}</span>
        </div>
      }>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">{t("pages.editOrder.loadingOrder")}</h2>
            <p className="text-gray-600">{t("pages.editOrder.pleaseWaitWhileWe")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout headerContent={
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("common.orders")}</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">{t("pages.editOrder.order")}</span>
        </div>
      }>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">{t("pages.editOrder.orderNotFound")}</h2>
            <p className="text-gray-600">{t("pages.editOrder.theRequestedOrderCould")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const isDraft = order.orderStatus === "draft";
  const mode = viewMode && !isDraft ? "view" : isDraft ? "edit" : "view";

  const editProducts: SelectedProduct[] = (order.products as any[] || [])
    .map((op: any) => {
      const opId = op.productId || op.uid;
      const fullProduct = orderProducts.find((p) => p.uid === opId);
      if (!fullProduct) return null;
      return {
        ...fullProduct,
        quantity: op.quantity,
        unitPrice: op.unitPrice ?? fullProduct.currentPrice ?? 0,
        notes: op.notes,
      } as SelectedProduct;
    })
    .filter((p): p is SelectedProduct => p !== null);

  return (
    <NewOrderFlow
      key={`${order.uid}-${orderProducts.length}`}
      mode={mode}
      editOrder={{
        id: order.uid,
        title: order.title || "",
        description: order.description || "",
        fulfillmentType: (order.fulfillmentType as "delivery" | "pickup") || "delivery",
        notes: order.notes || "",
        products: editProducts,
        orderStatus: order.orderStatus,
      }}
      onBack={handleCancel}
      onStartNewOrder={handleStartNewOrder}
      onOrderSubmitted={handleOrderSubmitted}
      contextBanner={<PcOrderingForBanner />}
    />
  );
}
